import type { ReactElement } from "react";
import { memo, useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

import useGetPrivateDataOrEmpty from "../../hooks/useGetPrivateDataOrEmpty";
import useGetSortedPrivateData from "../../hooks/useGetSortedPrivateData";
import type { ContactSearchEntry } from "../../types/common";
import { ContactEntryType } from "../../types/common";

import CollapsibleUnderlineButton from "./CollapsibleUnderlineButton";
import ContactDetailItem from "./ContactDetailItem";

const COLLAPSIBLE_COUNT = 3;

interface IContactDetailsListProps {
    rowData: ContactSearchEntry;
    onCopy?: (entryType: ContactEntryType) => void;
    dataAutomationPrefix?: string;
}

const ContactDetailsList = ({ rowData, onCopy, dataAutomationPrefix }: IContactDetailsListProps): ReactElement => {
    const { isRevealed } = rowData;

    const [isExpanded, setIsExpanded] = useState(false);

    const privateData = useGetPrivateDataOrEmpty(rowData);
    const contactDetails = useGetSortedPrivateData(privateData);

    const [isShowMobilePhoneError, isShowDirectPhoneError] = useMemo((): [boolean, boolean] => {
        return [
            !!(
                contactDetails?.revealErrors?.hasMobilePhoneNumbersError &&
                !contactDetails?.phoneNumbers?.some((phone) => phone.internalType === ContactEntryType.Mobile)
            ),
            !!(
                contactDetails?.revealErrors?.hasDirectPhoneNumbersError &&
                !contactDetails?.phoneNumbers?.some((phone) => phone.internalType === ContactEntryType.Direct)
            ),
        ];
    }, [contactDetails]);

    const errorElement = useMemo(
        () => (
            <span
                dangerouslySetInnerHTML={{
                    __html: translate("si.contacts_search.table.column.contact-details.unavailable-phone.tooltip"),
                }}
            ></span>
        ),
        [],
    );

    const emailError = useMemo(
        () =>
            contactDetails?.revealErrors?.hasValidEmailsError ? (
                <ContactDetailItem
                    key={`${ContactEntryType.Email}_withError`}
                    isInvalid
                    contact={translate("si.common.unavailable")}
                    type={ContactEntryType.Email}
                    isRevealed={isRevealed}
                    dataAutomation={
                        dataAutomationPrefix ? `${dataAutomationPrefix}-${ContactEntryType.Email}-error` : undefined
                    }
                    infoTooltip={errorElement}
                />
            ) : null,
        [contactDetails, dataAutomationPrefix, errorElement, isRevealed],
    );

    const contentElements = useMemo(() => {
        return [
            contactDetails.emails?.length
                ? contactDetails.emails.map((email, index) => (
                      <ContactDetailItem
                          key={ContactEntryType.Email + email + index}
                          contact={email}
                          type={ContactEntryType.Email}
                          isRevealed={isRevealed}
                          dataAutomation={
                              dataAutomationPrefix
                                  ? `${dataAutomationPrefix}-${ContactEntryType.Email}-${index}`
                                  : undefined
                          }
                          onCopy={() => onCopy?.(ContactEntryType.Email)}
                      />
                  ))
                : emailError,

            isShowDirectPhoneError ? (
                <ContactDetailItem
                    key={`${ContactEntryType.Direct}_withError`}
                    isInvalid
                    contact={translate("si.common.unavailable")}
                    type={ContactEntryType.Direct}
                    isRevealed={isRevealed}
                    dataAutomation={
                        dataAutomationPrefix ? `${dataAutomationPrefix}-${ContactEntryType.Direct}-error` : undefined
                    }
                    infoTooltip={errorElement}
                />
            ) : null,

            contactDetails.phoneNumbers?.length
                ? contactDetails.phoneNumbers.map((phoneNumber, index) => (
                      <ContactDetailItem
                          key={ContactEntryType.Mobile + phoneNumber.number + index}
                          contact={phoneNumber.number}
                          type={phoneNumber.internalType}
                          isRevealed={isRevealed}
                          dataAutomation={
                              dataAutomationPrefix
                                  ? `${dataAutomationPrefix}-${phoneNumber.internalType}-${index}`
                                  : undefined
                          }
                          infoTooltip={
                              phoneNumber.isDoNotCall
                                  ? translate("si.contacts_search.table.column.contact-details.dnc-phone.tooltip")
                                  : null
                          }
                          infoIconName={phoneNumber.isDoNotCall ? "block" : undefined}
                          infoIconColor={phoneNumber.isDoNotCall ? colorsPalettes.red["400_P"] : undefined}
                          onCopy={() =>
                              onCopy?.(
                                  phoneNumber.internalType === ContactEntryType.Mobile
                                      ? ContactEntryType.Mobile
                                      : ContactEntryType.Direct,
                              )
                          }
                      />
                  ))
                : null,

            isShowMobilePhoneError ? (
                <ContactDetailItem
                    key={`${ContactEntryType.Mobile}_withError`}
                    isInvalid
                    contact={translate("si.common.unavailable")}
                    type={ContactEntryType.Mobile}
                    isRevealed={isRevealed}
                    dataAutomation={
                        dataAutomationPrefix ? `${dataAutomationPrefix}-${ContactEntryType.Mobile}-error` : undefined
                    }
                    infoTooltip={errorElement}
                />
            ) : null,
        ]
            .flat()
            .filter(Boolean);
    }, [
        dataAutomationPrefix,
        contactDetails.emails,
        contactDetails.phoneNumbers,
        emailError,
        isShowDirectPhoneError,
        isRevealed,
        errorElement,
        isShowMobilePhoneError,
        onCopy,
    ]);

    const isCollapsible = contentElements.length > COLLAPSIBLE_COUNT;

    const contentElementsToRender = useMemo(
        () => (isCollapsible && !isExpanded ? contentElements.slice(0, COLLAPSIBLE_COUNT - 1) : contentElements),
        [isCollapsible, isExpanded, contentElements],
    );

    const expandButtonText = isExpanded
        ? translate("universal.button.showless")
        : translate("universal.button.show-more.count", {
              count: contentElements.length - contentElementsToRender.length,
          });

    return (
        <Flex
            flexDirection="column"
            gap="4px"
            width="100%"
        >
            {...contentElementsToRender}

            {isCollapsible ? (
                <CollapsibleUnderlineButton
                    isCollapsed={!isExpanded}
                    onClick={() => setIsExpanded((prevIsExpanded) => !prevIsExpanded)}
                >
                    {expandButtonText}
                </CollapsibleUnderlineButton>
            ) : null}
        </Flex>
    );
};

export default memo(ContactDetailsList);
